import type { BuildingDef, UnitDef } from '../data/types';
import type { TilePoint } from '../map/pathfinding';

export interface Building {
  readonly id: number;
  readonly owner: number;
  readonly def: BuildingDef;
  /** Top-left tile of the footprint. */
  readonly x: number;
  readonly y: number;
  /** Remaining resource for mines; 0 once collapsed. */
  gold: number;
  /** Hit points; null = cannot be attacked. */
  hp: number | null;
  /** Production queue; the front entry is the one in progress. */
  readonly queue: TrainEntry[];
  /** Null once complete. Ticks left count down only while a builder stands beside the site. */
  construction: { ticksLeft: number } | null;
}

export function isComplete(b: Building): boolean {
  return b.construction === null;
}

/** 0..1 construction progress (1 when complete). */
export function buildProgress(b: Building): number {
  if (!b.construction || b.def.buildTicks === 0) return 1;
  return 1 - b.construction.ticksLeft / b.def.buildTicks;
}

export interface TrainEntry {
  readonly def: UnitDef;
  ticksLeft: number;
}

export const MAX_QUEUE = 5;

export function footprintContains(b: Building, tx: number, ty: number): boolean {
  return tx >= b.x && ty >= b.y && tx < b.x + b.def.width && ty < b.y + b.def.height;
}

/** The footprint tile closest to (tx, ty): the tile itself when inside, else the clamped edge. */
export function nearestFootprintTile(b: Building, tx: number, ty: number): TilePoint {
  return {
    x: Math.max(b.x, Math.min(b.x + b.def.width - 1, tx)),
    y: Math.max(b.y, Math.min(b.y + b.def.height - 1, ty)),
  };
}

/** True when (tx, ty) touches the footprint (8-neighborhood) without being inside it. */
export function isAdjacentToFootprint(b: Building, tx: number, ty: number): boolean {
  if (footprintContains(b, tx, ty)) return false;
  const n = nearestFootprintTile(b, tx, ty);
  return Math.max(Math.abs(n.x - tx), Math.abs(n.y - ty)) === 1;
}
